import { useState } from 'react';

import { LearnHeader } from './LearnHeader';
import { ContinueLearningCard } from './ContinueLearningCard';
import { UpcomingSessionCard } from './UpcomingSessionCard';
import { ProgressSummaryCard } from './ProgressSummaryCard';
import { ModuleRow } from './ModuleRow';
import { buildLearnModel } from './learnModel';

import { useBatchCurriculum } from '@/lib/hooks/useLms';
import { useHome } from '@/lib/hooks/useHome';

export function LearnPage({ batchId }: { batchId: string }) {
  const { data: curriculum, isLoading, isError } = useBatchCurriculum(batchId);
  const { data: home } = useHome(batchId);
  const [openId, setOpenId] = useState<string | null>(null);

  if (isError) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 text-center">
        <p className="text-sm font-medium text-[#111827]">This course isn&apos;t available to you.</p>
        <p className="text-sm text-[#9ca3af]">You may not be enrolled in this batch.</p>
      </div>
    );
  }
  if (isLoading || !curriculum) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#8b5cf6] border-t-transparent" />
      </div>
    );
  }

  const model = buildLearnModel(curriculum);
  const defaultOpen = model.resume?.module.id ?? null;
  const effectiveOpen = openId ?? defaultOpen;

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-7 sm:px-6">
      <LearnHeader batchId={batchId} batchName={curriculum.batch_name} />

      <div className="pt-6">
        <ContinueLearningCard batchId={batchId} resume={model.resume} />
      </div>

      <div className="grid grid-cols-1 gap-6 pt-6 lg:grid-cols-2">
        <UpcomingSessionCard session={home?.next_session ?? null} />
        <ProgressSummaryCard completedModules={model.completedModules} totalModules={model.totalModules} overallPct={model.overallPct} />
      </div>

      <h2 className="pt-8 text-xl font-bold text-[#111827]">Modules</h2>
      <div className="mt-4 overflow-hidden rounded-[24px] border border-[#ececec] bg-white shadow-[0px_1px_12px_rgba(0,0,0,0.04)]">
        {model.modules.map((am, i) => (
          <ModuleRow
            key={am.module.id}
            batchId={batchId}
            index={i + 1}
            annotated={am}
            open={effectiveOpen === am.module.id}
            onToggle={() => setOpenId(effectiveOpen === am.module.id ? '' : am.module.id)}
          />
        ))}
      </div>
    </div>
  );
}
